// apps/patient/src/pages/messages/MessageInbox.tsx

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, 
  Plus, 
  Search, 
  Filter, 
  AlertCircle,
  X
} from 'lucide-react';
import { patientApiService as apiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import { useDebounce } from '@prism-ehr/shared';
import ThreadList from '../../components/messages/ThreadList';
import ThreadFilters from '../../components/messages/ThreadFilters';
import InlineThreadView from '../../components/messages/InlineThreadView';
import { cn } from '../../lib/utils';

export default function MessageInbox() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { patient } = usePatient();
  
  // State
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  
  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Simplified filters for patients
  const filters = {
    status: searchParams.getAll('status'),
    category: searchParams.getAll('category'),
    unread_only: searchParams.get('unread_only') === 'true',
    has_attachments: searchParams.get('has_attachments') === 'true'
  };
  
  // Fetch threads -
  const fetchThreads = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      console.time('[threads] getMessageThreads');
      const response = await apiService.getMessageThreads({
        page,
        limit: 20,
        sort_by: 'last_message_at',
        sort_direction: 'desc'
        // no filters
      });
      console.timeEnd('[threads] getMessageThreads');
      setThreads(response.items || []);
      setTotalPages(response.pages_total || 1);
      setTotalCount(response.items_total || 0);
    } catch (err) {
      console.error('Error fetching threads:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      const t1 = performance.now();
      setLoading(false);
    }
  }, [page]);
  
  // Initial load and refresh
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);
  
  // Polling for updates
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) fetchThreads();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchThreads]);
  
  // Search functionality
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        setSearchResults(null);
        return;
      }
      
      setIsSearching(true);
      
      try {
        const results = await apiService.searchThreads(debouncedSearchQuery, {
          limit: 10
        });
        setSearchResults(results.items || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    };
    
    performSearch();
  }, [debouncedSearchQuery]);
  
  // Handle thread selection
  const handleThreadSelect = (thread) => {
    setSelectedThread(thread);
    
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set('thread', thread.id);
    setSearchParams(newParams);
  };
  
  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (Array.isArray(value)) {
      // Remove all existing values for this filter
      newParams.delete(filterType);
      // Add new values
      value.forEach(v => newParams.append(filterType, v));
    } else if (typeof value === 'boolean') {
      if (value) {
        newParams.set(filterType, 'true');
      } else {
        newParams.delete(filterType);
      }
    } else if (value) {
      newParams.set(filterType, value);
    } else {
      newParams.delete(filterType);
    }
    
    setSearchParams(newParams);
    setPage(1); // Reset to first page when filters change
  };
  
  // Mark thread as read
  const handleMarkRead = async (threadId) => {
    try {
      await apiService.markThreadRead(threadId);
      
      // Update local state
      setThreads(prevThreads => 
        prevThreads.map(t => 
          t.id === threadId ? { ...t, unread_count: 0 } : t
        )
      );
      
      if (selectedThread?.id === threadId) {
        setSelectedThread(prev => ({ ...prev, unread_count: 0 }));
      }
    } catch (err) {
      console.error('Error marking thread as read:', err);
    }
  };

  const isThreadUnread = (thread: any) => {
    if (!patient?.id || !thread.participants) return false;
    if (thread.last_sender_id === patient.id) return false;

    const currentUserParticipant = thread.participants.find(
      p => p.user_id === patient.id
    );

    if (!currentUserParticipant) return false;

    return thread.last_message_at > (currentUserParticipant.last_read_at || 0);
  };
  
  // Get displayed threads (search results or regular list)
  const displayedThreads = searchResults || threads;
  
  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <MessageSquare className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            {totalCount > 0 && (
              <span className="text-sm text-gray-500">
                ({totalCount} {totalCount === 1 ? 'conversation' : 'conversations'})
              </span>
            )}
          </div>
          
          <button
            onClick={() => navigate('/messages/compose')}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Message</span>
          </button>
        </div>
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Thread List */}
        <div className={cn(
          "w-full lg:w-96 bg-white border-r flex flex-col",
          selectedThread && "hidden lg:flex"
        )}>
          {/* Search and Filters */}
          <div className="p-4 border-b space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors",
                showFilters ? "bg-primary text-white" : "text-gray-700 hover:bg-gray-100"
              )}
            >
              <Filter className="h-4 w-4" />
              Filters
              {(filters.status.length > 0 || filters.category.length > 0 || 
                filters.unread_only || filters.has_attachments) && (
                <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                  {filters.status.length + filters.category.length + 
                   (filters.unread_only ? 1 : 0) + (filters.has_attachments ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
          
          {/* Filters Panel */}
          {showFilters && (
            <ThreadFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              simplified={true} // Patient-specific prop
            />
          )}
          
          {/* Thread List */}
          <div className="flex-1 overflow-y-auto">
            {loading && !threads.length ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : displayedThreads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                <MessageSquare className="h-8 w-8 mb-2" />
                <p className="text-sm">
                  {searchQuery ? 'No messages found' : 'No conversations yet'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => navigate('/messages/compose')}
                    className="mt-2 text-primary hover:underline text-sm"
                  >
                    Start a new conversation
                  </button>
                )}
              </div>
            ) : (
              <ThreadList
                threads={displayedThreads}
                selectedThread={selectedThread}
                onThreadSelect={handleThreadSelect}
                onMarkRead={handleMarkRead}
                isThreadUnread={isThreadUnread}
              />
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
        
        {/* Main Content - Thread View */}
        <div className={cn(
          "flex-1 flex flex-col",
          !selectedThread && "hidden lg:flex"
        )}>
          {selectedThread ? (
            <>
              {/* Mobile back button */}
              <div className="lg:hidden border-b bg-white px-4 py-2">
                <button
                  onClick={() => setSelectedThread(null)}
                  className="text-primary hover:underline text-sm"
                >
                  ← Back to messages
                </button>
              </div>
              
              <InlineThreadView
                thread={selectedThread}
                onRefresh={fetchThreads}
                isPatient={true}
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm mt-1">Choose a message thread to view the conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}